import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, ArrowsClockwise, CalendarBlank } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";

/**
 * Admin panel: manage public holidays. Any date in this list is skipped from
 * leave-day counts (in addition to Oman weekends — Fri + Sat). One-click
 * "seed Oman" pushes national/secular fixtures for the chosen year; Islamic
 * dates are added manually since they depend on moon-sighting.
 */
export default function HolidaysPanel() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ date: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [seedYear, setSeedYear] = useState(new Date().getFullYear());

  const load = async () => {
    try {
      const { data } = await api.get("/hr/holidays");
      setRows(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load holidays");
    }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.date || !form.name.trim()) return toast.error("Date and name are required");
    setBusy(true);
    try {
      await api.post("/hr/holidays", { date: form.date, name: form.name.trim() });
      toast.success("Holiday saved");
      setForm({ date: "", name: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (iso) => {
    if (!window.confirm(`Remove holiday on ${iso}?`)) return;
    try {
      await api.delete(`/hr/holidays/${iso}`);
      toast.success("Holiday removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const seed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/hr/holidays/seed-oman?year=${seedYear}`);
      toast.success(`Seeded ${data.added} Oman holidays for ${data.year}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <section className="border border-zinc-950 mt-8" data-testid="holidays-panel">
      <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center justify-between">
        <span>public holidays</span>
        <button onClick={load} className="text-white/60 hover:text-white flex items-center gap-1" title="Reload">
          <ArrowsClockwise size={11} />
        </button>
      </div>
      <div className="p-5">
        <p className="text-sm text-zinc-600 mb-4 max-w-2xl">
          Days listed here (and Oman weekends Fri + Sat) never consume leave entitlement.
          Add or import the year&apos;s calendar so a 7-day trip during a national holiday isn&apos;t billed as 7 working days.
        </p>

        <form onSubmit={add} className="grid gap-3 md:grid-cols-[160px_1fr_auto] mb-5 items-end" data-testid="holiday-form">
          <div>
            <label className="label-sm text-zinc-500">Date</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              data-testid="holiday-date-input"
              className="block w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-1 text-sm outline-none"
            />
          </div>
          <div>
            <label className="label-sm text-zinc-500">Name</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Eid Al Fitr Day 1"
              data-testid="holiday-name-input"
              className="block w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-1 text-sm outline-none"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="holiday-add-btn"
            className="bg-zinc-950 text-white text-xs font-semibold px-5 py-2 hover:bg-[#FF3E00] disabled:opacity-40 flex items-center gap-1"
          >
            <Plus size={12} /> ADD HOLIDAY
          </button>
        </form>

        <div className="border border-zinc-200 mb-4">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-950">
              <tr className="text-xs tracking-wider uppercase">
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="w-16" />
              </tr>
            </thead>
            <tbody data-testid="holidays-tbody">
              {rows.length === 0 && (
                <tr><td colSpan={3} className="px-3 py-6 text-center text-xs font-mono text-zinc-400">NO HOLIDAYS — ADD ONE ABOVE OR SEED BELOW</td></tr>
              )}
              {rows.map((h) => (
                <tr key={h.date} className="border-b border-zinc-100">
                  <td className="px-3 py-2 tnum text-xs font-mono">{h.date}</td>
                  <td className="px-3 py-2">{h.name || <span className="text-zinc-400">—</span>}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => remove(h.date)}
                      className="text-zinc-400 hover:text-[#D0021B]"
                      title="Remove"
                      data-testid={`holiday-remove-${h.date}`}
                    >
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center gap-3 pt-2 border-t border-zinc-100">
          <CalendarBlank size={14} className="text-zinc-500" />
          <span className="text-xs font-mono text-zinc-500">SEED OMAN NATIONAL HOLIDAYS:</span>
          <input
            type="number"
            value={seedYear}
            onChange={(e) => setSeedYear(Number(e.target.value) || new Date().getFullYear())}
            min="2024"
            max="2035"
            className="w-24 border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-1 text-sm tnum outline-none"
            data-testid="holiday-seed-year"
          />
          <button
            onClick={seed}
            disabled={busy}
            className="text-xs font-semibold border border-zinc-950 px-3 py-1.5 hover:bg-zinc-950 hover:text-white disabled:opacity-40"
            data-testid="holiday-seed-btn"
          >
            SEED
          </button>
          <span className="text-[10px] font-mono text-zinc-400 ml-2">Islamic dates need manual entry (moon-dependent)</span>
        </div>
      </div>
    </section>
  );
}
